// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2025-2026 Daragh Farrell

interface LogoProps {
  variant?: 'icon' | 'wordmark' | 'full';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const sizes = {
  sm: { icon: 20, text: 'text-sm' },
  md: { icon: 28, text: 'text-lg' },
  lg: { icon: 36, text: 'text-2xl' },
};

/**
 * ClubView logo component.
 * Variants: icon (standalone mark), wordmark (text only), full (icon + text).
 * The SVG icon is a diamond with horizontal line (viewfinder motif).
 */
export default function Logo({ variant = 'full', size = 'md', className = '' }: LogoProps) {
  const s = sizes[size];

  const icon = (
    <svg
      width={s.icon}
      height={s.icon}
      viewBox="0 0 100 100"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      className="flex-shrink-0"
    >
      <path
        d="M50 8 L92 50 L50 92 L8 50 Z"
        stroke="currentColor"
        strokeWidth="5"
        strokeLinejoin="round"
        strokeLinecap="round"
        fill="none"
      />
      <line
        x1="18"
        y1="50"
        x2="82"
        y2="50"
        stroke="currentColor"
        strokeWidth="5"
        strokeLinecap="round"
      />
    </svg>
  );

  const wordmark = (
    <span className={`font-light tracking-wide ${s.text} leading-none`}>
      clubview
    </span>
  );

  if (variant === 'icon') {
    return <span className={`inline-flex items-center text-brand-600 ${className}`}>{icon}</span>;
  }

  if (variant === 'wordmark') {
    return <span className={`inline-flex items-center text-slate-800 ${className}`}>{wordmark}</span>;
  }

  // full
  return (
    <span className={`inline-flex items-center gap-2 ${className}`}>
      <span className="text-brand-600">{icon}</span>
      <span className="text-slate-800">{wordmark}</span>
    </span>
  );
}
